import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  LinearProgress,
  Pagination,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { buildAudienceNarrative, buildAudienceStatusBadges, isManualAudience, normalizePositiveInteger, toAudienceRow } from "./audienceUtils";
import { emailContactsApi } from "./api/emailContactsApi";
import AudienceTable from "./AudienceTable";

const REASON_LABELS = {
  budget_exceeded: "Monthly/daily budget limit reached",
  recent_touch: "Contacted too recently",
  topic_cooldown: "Same topic sent recently",
  campaign_limit_reached: "Preferred send limit reached",
  duplicate_campaign: "Already received identical message",
};

export default function AudienceReview({
  audienceChoice,
  source,
  filters,
  audiencePlan,
  audienceSummary,
  previewRows,
  audienceById,
  setAudienceById,
  selectedIds,
  setSelectedIds,
  suppressedEmails,
  excludeRecent,
  recentDays,
  onGoBack,
  onSampleExclude,
  onReviewBarChange,
}) {
  const summaryBadges = buildAudienceStatusBadges(audienceSummary);
  const narrative = buildAudienceNarrative({
    audienceKey: audienceChoice,
    source,
    filters,
    summary: audienceSummary,
  });
  const manualSelection = isManualAudience(audienceChoice);
  const preferredLimit = normalizePositiveInteger(audienceSummary?.preferredLimit || filters?.preferred_limit);
  const sampleRows = useMemo(() => previewRows || [], [previewRows]);
  const isImport = source === "import";
  // "My Contacts" is an audience_kind, not a segment. The global /email/contacts list
  // endpoint doesn't understand audience_kind and would return the ENTIRE pool, so the
  // review table would show thousands of strangers while the header says "Will send 1".
  // Render the estimate's own sample (previewRows) instead, matching the will-send count.
  const isMyContacts =
    audienceChoice === "my_contacts" || audiencePlan?.filters?.audience_kind === "my_contacts";
  // Import renders its parsed rows locally. Everything else — including my_contacts —
  // fetches from the server (paginated). my_contacts passes audience_kind so the backend
  // returns the owner's personal contacts instead of the entire global pool.
  const usePreviewRows = isImport;

  // Pagination state for non-manual, non-import audiences
  const [pageNum, setPageNum] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalContacts, setTotalContacts] = useState(() => Number(audienceSummary?.eligibleFound || 0));
  const [loadingRows, setLoadingRows] = useState(false);
  const [currentPageRows, setCurrentPageRows] = useState([]);
  const [allLoadedRows, setAllLoadedRows] = useState([]);

  useEffect(() => {
    // Skip the global pool fetch for manual/import/my_contacts — those render previewRows.
    if (manualSelection || usePreviewRows || !audiencePlan) return;
    let alive = true;
    setLoadingRows(true);
    const pf = audiencePlan.filters || {};
    emailContactsApi.list({
      q: pf.q || "",
      source: pf.source || "",
      segment: pf.segment || "",
      active: "1",
      excludeRecent: pf.exclude_recent ?? excludeRecent,
      days: pf.days ?? Number(recentDays || 7),
      audienceKind: pf.audience_kind || (isMyContacts ? "my_contacts" : null),
      perPage: 100,
      page: pageNum,
    }).then((res) => {
      if (!alive) return;
      const paginated = res?.contacts || res;
      const rows = Array.isArray(paginated?.data) ? paginated.data : [];
      const mapped = rows.map(toAudienceRow);
      const meta = paginated?.meta || paginated;
      setCurrentPageRows(mapped);
      if (meta?.total) setTotalContacts(Number(meta.total));
      if (meta?.last_page) setTotalPages(Number(meta.last_page));
      setAllLoadedRows((prev) => {
        const seen = new Set(prev.map((r) => r.id));
        return [...prev, ...mapped.filter((r) => !seen.has(r.id))];
      });
    }).catch(() => {}).finally(() => { if (alive) setLoadingRows(false); });
    return () => { alive = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNum, audiencePlan, usePreviewRows, manualSelection]);

  // Rows shown in the table: previewRows for import/my_contacts, else the fetched page.
  const tableRows = usePreviewRows ? sampleRows : currentPageRows;

  // Sample removal tracking (non-manual audiences)
  const [sampleRemovedIds, setSampleRemovedIds] = useState(() => new Set());
  const toggleSampleRow = useCallback((id) => {
    setSampleRemovedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);
  const toggleAllSample = useCallback((checked) => {
    setSampleRemovedIds((prev) => {
      const next = new Set(prev);
      const ids = tableRows.map((r) => r.id);
      if (checked) {
        ids.forEach((id) => next.delete(id));
      } else {
        ids.forEach((id) => next.add(id));
      }
      return next;
    });
  }, [tableRows]);

  useEffect(() => {
    const allRows = usePreviewRows ? sampleRows : allLoadedRows;
    onSampleExclude?.({ removedIds: sampleRemovedIds, allRows });
  }, [sampleRemovedIds, allLoadedRows, sampleRows, onSampleExclude, usePreviewRows]);

  useEffect(() => {
    onReviewBarChange?.({
      pageNum,
      totalPages,
      totalContacts,
      removedCount: sampleRemovedIds.size,
      isImport,
      currentPageCount: tableRows.length,
      onPageChange: setPageNum,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNum, totalPages, totalContacts, sampleRemovedIds.size, isImport, tableRows.length, onReviewBarChange]);

  const allSampleChecked = tableRows.length > 0 && tableRows.every((r) => !sampleRemovedIds.has(r.id));
  const someSampleChecked = tableRows.some((r) => sampleRemovedIds.has(r.id)) && !tableRows.every((r) => sampleRemovedIds.has(r.id));
  const keptCount = usePreviewRows
    ? sampleRows.length - sampleRemovedIds.size
    : allLoadedRows.length - sampleRemovedIds.size;
  const groupedReasons = useMemo(() => {
    if (audienceSummary?.suppressedBreakdown && typeof audienceSummary.suppressedBreakdown === "object") {
      return Object.entries(audienceSummary.suppressedBreakdown)
        .filter(([key]) => !key.toLowerCase().includes("budget"));
    }

    const counts = new Map();
    (suppressedEmails || []).forEach((item) => {
      const label = item?.reason_label || "Suppressed";
      counts.set(label, (counts.get(label) || 0) + 1);
    });
    return Array.from(counts.entries());
  }, [audienceSummary?.suppressedBreakdown, suppressedEmails]);

  const budgetExceededCount = useMemo(() => {
    const breakdown = audienceSummary?.suppressedBreakdown || {};
    return Object.entries(breakdown)
      .filter(([key]) => key.toLowerCase().includes("budget"))
      .reduce((sum, [, count]) => sum + Number(count), 0);
  }, [audienceSummary?.suppressedBreakdown]);

  const [budgetSnackOpen, setBudgetSnackOpen] = useState(false);
  useEffect(() => {
    if (budgetExceededCount > 0) setBudgetSnackOpen(true);
  }, [budgetExceededCount]);

  return (
    <>
      <Paper elevation={0} sx={{ border: "1px solid #E5E7EB", borderRadius: 3, overflow: "hidden" }}>

        {/* ── Summary bar ── */}
        <Box sx={{ px: 2, pt: 2, pb: 1.5 }}>
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="subtitle2" fontWeight={900} sx={{ mr: 0.5 }}>
                Review this audience
              </Typography>
              <Chip size="small" label={`Matched ${summaryBadges.matched.toLocaleString()}`} />
              <Chip size="small" color="primary" label={`Will send ${summaryBadges.sendable.toLocaleString()}`} />
              <Chip size="small" color="warning" label={`Will skip ${summaryBadges.skipped.toLocaleString()}`} />
              {budgetExceededCount > 0 ? (
                <Tooltip title="Monthly/daily budget limit reached. Contact IT to increase it." arrow>
                  <Chip
                    size="small"
                    color="error"
                    variant="outlined"
                    label={`Budget limit: ${budgetExceededCount.toLocaleString()}`}
                  />
                </Tooltip>
              ) : null}
              {manualSelection ? (
                <Chip size="small" color="success" variant="outlined" label={`Selected ${selectedIds.length.toLocaleString()}`} />
              ) : null}
              {preferredLimit ? (
                <Chip size="small" variant="outlined" label={`Limit ${preferredLimit.toLocaleString()}`} />
              ) : null}
            </Stack>

            <Typography variant="caption" color="text.secondary">
              {narrative}
            </Typography>

            {groupedReasons.length > 0 ? (
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                {groupedReasons.map(([label, count]) => (
                  <Tooltip key={label} title={REASON_LABELS[label] || label} arrow>
                    <Chip size="small" variant="outlined" label={`${label}: ${count}`} />
                  </Tooltip>
                ))}
              </Stack>
            ) : null}

            {onGoBack && Number(audienceSummary?.suppressedFound) > Number(audienceSummary?.eligibleFound) ? (
              <Button size="small" onClick={onGoBack} startIcon={<ArrowBackIcon />} sx={{ alignSelf: "flex-start" }}>
                Adjust filters
              </Button>
            ) : null}
          </Stack>
        </Box>

        <Divider />

        {/* ── Recipient table ── */}
        {!manualSelection ? (
          <Box sx={{ px: 2, pt: 1, pb: 2 }}>
            {!isImport && sampleRemovedIds.size > 0 ? (
              <Alert severity="info" sx={{ py: 0.5, mb: 1 }}>
                <strong>{sampleRemovedIds.size.toLocaleString()} contact{sampleRemovedIds.size !== 1 ? "s" : ""} excluded.</strong>{" "}
                The full audience will still be resolved at send time — only these specific contacts will be skipped.
              </Alert>
            ) : null}

            {loadingRows ? <LinearProgress sx={{ borderRadius: 1, mb: 1 }} /> : null}

            {tableRows.length === 0 && !loadingRows ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                No recipients available. Go back and run Find Audience.
              </Typography>
            ) : (
              <TableContainer component={Box} sx={{ border: "1px solid #E5E7EB", borderRadius: 2, height: "calc(100vh - 195px)", minHeight: 280, overflow: "auto" }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" sx={{ width: 48 }}>
                        <Tooltip title={allSampleChecked ? "Remove all on this page" : "Restore all on this page"} arrow>
                          <Checkbox
                            checked={allSampleChecked}
                            indeterminate={someSampleChecked}
                            onChange={(e) => toggleAllSample(e.target.checked)}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 900, width: "35%" }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: 900 }}>Email</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tableRows.map((row) => {
                      const removed = sampleRemovedIds.has(row.id);
                      return (
                        <TableRow
                          key={row.id}
                          hover
                          selected={!removed}
                          onClick={() => toggleSampleRow(row.id)}
                          sx={{ cursor: "pointer", opacity: removed ? 0.4 : 1 }}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox checked={!removed} />
                          </TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {row.name || "—"}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {row.email}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        ) : (
          <Box sx={{ p: 2 }}>
            <Stack spacing={1.5}>
              <Typography variant="subtitle2" fontWeight={900}>
                Pick recipients for this custom audience
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Custom audiences are the only path where the sender needs to choose individual contacts. Everyone else can just use the audience outcome and keep moving.
              </Typography>
              <Divider />
              <AudienceTable
                source={source}
                filters={filters}
                audienceById={audienceById}
                setAudienceById={setAudienceById}
                selectedIds={selectedIds}
                setSelectedIds={setSelectedIds}
                previewTotalFound={audienceSummary?.totalFound || 0}
                previewRows={previewRows}
                estimateSummary={audienceSummary}
                suppressedEmails={suppressedEmails}
                excludeRecent={excludeRecent}
                recentDays={recentDays}
              />
            </Stack>
          </Box>
        )}
      </Paper>

      <Snackbar
        open={budgetSnackOpen}
        autoHideDuration={8000}
        onClose={() => setBudgetSnackOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="warning" onClose={() => setBudgetSnackOpen(false)} sx={{ width: "100%" }}>
          <strong>{budgetExceededCount.toLocaleString()} contact{budgetExceededCount !== 1 ? "s" : ""}</strong> excluded — broadcast budget reached. Contact IT to increase it.
        </Alert>
      </Snackbar>
    </>
  );
}
